import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Popup, GeoJSON, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Box } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import { DESIGN_TOKENS } from '../../styles/theme';

// Fix for default Leaflet marker icons in React
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface LocationPickerMapProps {
  lat: number;
  lng: number;
  onLocationSelect: (lat: number, lng: number) => void;
  markers?: Array<{
    lat: number;
    lng: number;
    label?: string;
    pointIndex?: number;
  }>;
  parkingPoints?: Array<{
    id: string | number;
    lat: number;
    lng: number;
    name?: string;
    hgv?: string;
    operator?: string;
  }>;
  polylinePositions?: Array<[number, number]>;
  geojson?: any;
  height?: number;
  onMarkerMove?: (pointIndex: number, lat: number, lng: number) => void;
}

function MapViewport({
  lat,
  lng,
  markers,
  geojson,
}: Pick<LocationPickerMapProps, 'lat' | 'lng' | 'markers' | 'geojson'>) {
  const map = useMap();

  useEffect(() => {
    const validMarkers = (markers ?? []).filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));
    const geojsonBounds = geojson ? L.geoJSON(geojson).getBounds() : null;

    if (validMarkers.length > 1) {
      const bounds = L.latLngBounds(validMarkers.map((marker) => [marker.lat, marker.lng] as [number, number]));
      if (geojsonBounds?.isValid()) {
        bounds.extend(geojsonBounds);
      }
      map.fitBounds(bounds, { padding: [24, 24] });
      return;
    }

    if (geojsonBounds?.isValid()) {
      map.fitBounds(geojsonBounds, { padding: [24, 24] });
      return;
    }

    if (validMarkers.length === 1) {
      map.setView([validMarkers[0].lat, validMarkers[0].lng], Math.max(map.getZoom(), 13));
      return;
    }

    if (lat && lng) {
      map.setView([lat, lng], map.getZoom());
    }
  }, [lat, lng, map, markers]);

  return null;
}

function MapClickHandler({
  onLocationSelect,
}: Pick<LocationPickerMapProps, 'onLocationSelect'>) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  return null;
}

// Internal component to handle marker placement
function LocationMarker({
  lat,
  lng,
}: Pick<LocationPickerMapProps, 'lat' | 'lng'>) {
  return lat && lng ? <Marker position={[lat, lng]} /> : null;
}

export const LocationPickerMap: React.FC<LocationPickerMapProps> = ({
  lat,
  lng,
  onLocationSelect,
  markers,
  parkingPoints,
  polylinePositions,
  geojson,
  height = 300,
  onMarkerMove,
}) => {
  const defaultPosition: [number, number] = [52.2297, 21.0122]; // Warsaw
  const hasCustomMarkers = Boolean(markers?.length);
  const validPolylinePositions = (polylinePositions ?? []).filter(
    (position) => Number.isFinite(position[0]) && Number.isFinite(position[1])
  );
  const validMarkers = (markers ?? []).filter((marker) => Number.isFinite(marker.lat) && Number.isFinite(marker.lng));
  const validParkingPoints = (parkingPoints ?? []).filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

  return (
    <Box
      sx={{
        height,
        width: '100%',
        borderRadius: DESIGN_TOKENS.borderRadius.card,
        overflow: 'hidden',
        border: `1px solid ${DESIGN_TOKENS.colors.border}`,
        mt: 1,
        zIndex: 1,
        position: 'relative'
      }}
    >
      <MapContainer
        center={lat && lng ? [lat, lng] : defaultPosition}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapClickHandler onLocationSelect={onLocationSelect} />
        {!hasCustomMarkers ? (
          <LocationMarker lat={lat} lng={lng} />
        ) : (
          <>
            {validMarkers.map((marker, index) => (
              <Marker
                key={`${marker.pointIndex ?? index}-${marker.lat}-${marker.lng}`}
                position={[marker.lat, marker.lng]}
                draggable={typeof marker.pointIndex === 'number' && Boolean(onMarkerMove)}
                eventHandlers={{
                  dragend: (event) => {
                    if (typeof marker.pointIndex !== 'number' || !onMarkerMove) return;
                    const position = (event.target as L.Marker).getLatLng();
                    onMarkerMove(marker.pointIndex, position.lat, position.lng);
                  },
                }}
              >
                {marker.label ? (
                  <Popup>{marker.label}</Popup>
                ) : null}
              </Marker>
            ))}
          </>
        )}
        {validParkingPoints.map((parking) => (
          <CircleMarker
            key={`parking-${parking.id}`}
            center={[parking.lat, parking.lng]}
            radius={7}
            pathOptions={{
              color: '#ffffff',
              weight: 2,
              fillColor: '#0f766e',
              fillOpacity: 0.95,
            }}
          >
            <Popup>
              <Box>
                <Box sx={{ fontWeight: 700 }}>
                  {parking.name || 'Parking dla ciężarówek'}
                </Box>
                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {parking.operator ? `Operator: ${parking.operator}` : 'Operator: nieznany'}
                </Box>
                <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {parking.hgv ? `HGV: ${parking.hgv}` : 'HGV: brak danych'}
                </Box>
              </Box>
            </Popup>
          </CircleMarker>
        ))}
        {validPolylinePositions.length > 1 ? (
          <Polyline
            positions={validPolylinePositions}
            color={DESIGN_TOKENS.colors.primary}
            weight={4}
            opacity={0.75}
          />
        ) : null}
        {geojson ? (
          <GeoJSON
            data={geojson}
            style={{
              color: DESIGN_TOKENS.colors.primary,
              weight: 5,
              opacity: 0.8,
            }}
          />
        ) : null}
        <MapViewport lat={lat} lng={lng} markers={validMarkers} geojson={geojson} />
      </MapContainer>
    </Box>
  );
};
